import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Avatar, VybePill, Ic, ICONS } from "../components/UI.jsx";
import { useAuthStore } from "../context/store.js";
import api from "../api/client.js";

const B = {
  bg:"#08070F",bg2:"#0D0C18",bg3:"#131124",
  violet:"#7C3AED",violetLt:"#8B5CF6",
  violetDim:"rgba(124,58,237,0.15)",violetBorder:"rgba(124,58,237,0.3)",
  vibeGreen:"#10F5A0",vibeGreenDim:"rgba(16,245,160,0.12)",
  coral:"#FF6B6B",sky:"#38BDF8",purple:"#A78BFA",amber:"#FFB830",
  text:"#F0EFFE",text2:"#7C7A9E",text3:"#3D3B5C",
  border:"rgba(124,58,237,0.15)",border2:"rgba(124,58,237,0.08)",
  grad:"linear-gradient(135deg,#7C3AED,#10F5A0)",gradCoral:"linear-gradient(135deg,#7C3AED,#FF6B6B)",
  font:"'Sora',system-ui,sans-serif",mono:"'Space Mono',monospace",
};

const timeAgo = d => { try { const m=Math.floor((Date.now()-new Date(d))/60000); return m<1?"now":m<60?`${m}m`:m<1440?`${Math.floor(m/60)}h`:`${Math.floor(m/1440)}d`; } catch { return ""; } };
const numFmt  = n => !n?"0":n>=1000?`${(n/1000).toFixed(1)}k`:String(n);

export default function ProfilePage() {
  const { handle }  = useParams();
  const { user: me, updateUser } = useAuthStore();
  const [profile,   setProfile]   = useState(null);
  const [vibes,     setVibes]     = useState([]);
  const [activeTab, setActiveTab] = useState("vibes");
  const [editing,   setEditing]   = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [editForm,  setEditForm]  = useState({ displayName:"",bio:"",role:"",location:"" });
  const [following, setFollowing] = useState(false);
  const [loading,   setLoading]   = useState(true);

  const isMe = !handle || handle === me?.handle?.replace("@","");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        if (isMe) {
          setProfile(me);
          setEditForm({ displayName:me?.displayName||"", bio:me?.bio||"", role:me?.role||"", location:me?.location||"" });
          const { data } = await api.get(`/users/${me?.handle||"me"}/vibes`);
          setVibes(data);
        } else {
          const { data } = await api.get(`/users/${handle}`);
          setProfile(data);
          const v = await api.get(`/users/${handle}/vibes`);
          setVibes(v.data);
          setFollowing(data.followers?.includes(me?._id));
        }
      } catch {}
      setLoading(false);
    };
    if (me) load();
  }, [handle, me]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/users/me", editForm);
      updateUser(data);
      setProfile(data);
      setEditing(false);
    } catch {}
    setSaving(false);
  };

  const handleFollow = async () => {
    if (!profile?._id) return;
    setFollowing(v => !v);
    try { await api.post(`/users/${profile._id}/follow`); } catch {}
  };

  const displayProfile = profile || me;
  const stats = [
    { n: numFmt(displayProfile?.following?.length||0),  l:"Connecting" },
    { n: numFmt(displayProfile?.followers?.length||0),  l:"Connected"  },
    { n: vibes.length||0,                               l:"Vibes"      },
  ];

  const catColor = {"Tech Vibes":B.sky,"Global Connect":B.vibeGreen,"Creative Learn":B.amber,"Human Potential":B.purple,"AgriTech":B.vibeGreen,"DAO Governance":B.coral,"General":B.violet};

  if (loading) return (
    <div style={{ flex:1,display:"flex",alignItems:"center",justifyContent:"center",height:"100%" }}>
      <div className="vyl-spinner" />
    </div>
  );

  return (
    <div style={{ display:"flex",flexDirection:"column",height:"100%",overflow:"hidden" }}>
      {/* Page header */}
      <div style={{ display:"flex",alignItems:"center",gap:12,padding:"12px 16px",borderBottom:`1px solid ${B.border2}`,flexShrink:0 }}>
        <div>
          <div style={{ fontWeight:900,fontSize:20,color:B.text }}>{displayProfile?.displayName||"Profile"}</div>
          <div style={{ fontSize:12,color:B.text2,fontFamily:B.mono }}>{numFmt(vibes.length)} vibes</div>
        </div>
      </div>

      <div style={{ flex:1,overflowY:"auto" }}>
        {/* Banner */}
        <div style={{ height:130,background:displayProfile?.banner?`url(${displayProfile.banner}) center/cover`:B.grad,position:"relative",opacity:0.9 }}>
          <div style={{ position:"absolute",inset:0,background:`linear-gradient(to bottom,transparent,${B.bg2})` }} />
          <div style={{ position:"absolute",bottom:-42,left:16,border:`4px solid ${B.bg2}`,borderRadius:"50%",boxShadow:`0 0 24px ${displayProfile?.color||B.violet}40` }}>
            <Avatar user={displayProfile} size={76} ring />
          </div>
        </div>

        {/* Edit / Follow / Actions */}
        <div style={{ display:"flex",justifyContent:"flex-end",gap:8,padding:"10px 16px" }}>
          {isMe ? (
            editing ? (
              <>
                <button onClick={()=>setEditing(false)} style={{ padding:"8px 18px",borderRadius:9999,border:`1px solid ${B.border}`,background:"none",color:B.text2,fontFamily:B.font,fontSize:14,cursor:"pointer" }}>Cancel</button>
                <button onClick={handleSave} disabled={saving} style={{ padding:"8px 22px",borderRadius:9999,border:"none",background:B.grad,color:"#fff",fontFamily:B.font,fontWeight:700,fontSize:14,cursor:"pointer",boxShadow:"0 4px 16px rgba(124,58,237,0.4)",display:"flex",alignItems:"center",gap:6 }}>
                  {saving?<><div className="vyl-spinner" style={{width:14,height:14}} />Saving…</>:"Save Profile"}
                </button>
              </>
            ) : (
              <button onClick={()=>setEditing(true)} style={{ padding:"8px 20px",borderRadius:9999,border:`1px solid ${B.border}`,background:"none",color:B.text,fontFamily:B.font,fontWeight:700,fontSize:14,cursor:"pointer" }}>Edit Profile</button>
            )
          ) : (
            <>
              <button style={{ width:36,height:36,borderRadius:"50%",border:`1px solid ${B.border2}`,background:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center" }}>
                <Ic d={ICONS.chat} s={16} c={B.text2} />
              </button>
              <button onClick={handleFollow} style={{
                padding:"8px 22px",borderRadius:9999,fontWeight:700,fontSize:14,cursor:"pointer",fontFamily:B.font,transition:"all 0.2s",
                background:following?"transparent":B.grad,color:following?B.text:"#fff",
                border:following?`1px solid ${B.border}`:"none",
                boxShadow:following?"none":"0 4px 16px rgba(124,58,237,0.4)",
              }}>{following?"Connected ✓":"Connect"}</button>
            </>
          )}
        </div>

        {/* Profile info */}
        <div style={{ padding:"2px 16px 16px",borderBottom:`1px solid ${B.border2}`,marginTop:28 }}>
          {editing ? (
            <div style={{ display:"flex",flexDirection:"column",gap:10 }}>
              <input value={editForm.displayName} onChange={e=>setEditForm(f=>({...f,displayName:e.target.value}))} placeholder="Display name"
                style={{ background:B.bg3,border:`1px solid ${B.border}`,borderRadius:10,padding:"10px 14px",color:B.text,fontFamily:B.font,fontSize:15,outline:"none",width:"100%" }} />
              <input value={editForm.role} onChange={e=>setEditForm(f=>({...f,role:e.target.value}))} placeholder="Role / title"
                style={{ background:B.bg3,border:`1px solid ${B.border}`,borderRadius:10,padding:"10px 14px",color:B.text,fontFamily:B.font,fontSize:14,outline:"none",width:"100%" }} />
              <textarea value={editForm.bio} onChange={e=>setEditForm(f=>({...f,bio:e.target.value}))} placeholder="Your bio (max 280 chars)" rows={3}
                style={{ background:B.bg3,border:`1px solid ${B.border}`,borderRadius:10,padding:"10px 14px",color:B.text,fontFamily:B.font,fontSize:14,outline:"none",width:"100%",resize:"none" }} />
              <input value={editForm.location} onChange={e=>setEditForm(f=>({...f,location:e.target.value}))} placeholder="Location"
                style={{ background:B.bg3,border:`1px solid ${B.border}`,borderRadius:10,padding:"10px 14px",color:B.text,fontFamily:B.font,fontSize:14,outline:"none",width:"100%" }} />
            </div>
          ) : (
            <>
              <div style={{ fontWeight:900,fontSize:22,color:B.text }}>{displayProfile?.displayName}</div>
              <div style={{ display:"flex",alignItems:"center",gap:8,marginTop:3,flexWrap:"wrap" }}>
                <span style={{ color:B.text2,fontSize:14,fontFamily:B.mono }}>{displayProfile?.handle}</span>
                {displayProfile?.verified&&<span style={{ color:B.violet,fontSize:13 }}>✓ Verified</span>}
                {displayProfile?.online ? <span style={{ color:B.vibeGreen,fontSize:12 }}>● Online</span> : <span style={{ color:B.text3,fontSize:12 }}>● Offline</span>}
              </div>
              {displayProfile?.bio && <div style={{ fontSize:15,margin:"10px 0",color:B.text,lineHeight:1.6 }}>{displayProfile.bio}</div>}
              <div style={{ display:"flex",flexWrap:"wrap",gap:6,marginBottom:12 }}>
                {displayProfile?.role&&<VybePill label={displayProfile.role} color={displayProfile.color||B.violet} />}
                {displayProfile?.location&&<span style={{ fontSize:13,color:B.text2 }}>📍 {displayProfile.location}</span>}
              </div>
              <div style={{ display:"flex",gap:24 }}>
                {stats.map(s=>(
                  <div key={s.l}>
                    <span style={{ fontWeight:900,fontSize:16,background:B.grad,WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent" }}>{s.n} </span>
                    <span style={{ color:B.text2,fontSize:14 }}>{s.l}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Tabs */}
        <div style={{ display:"flex",borderBottom:`1px solid ${B.border2}`,flexShrink:0 }}>
          {["vibes","replies","media","likes"].map(t=>(
            <button key={t} onClick={()=>setActiveTab(t)} style={{
              flex:1,padding:"13px 0",textAlign:"center",fontSize:14,
              fontWeight:activeTab===t?700:400,color:activeTab===t?B.text:B.text2,
              border:"none",background:"none",cursor:"pointer",fontFamily:B.font,
              borderBottom:activeTab===t?`3px solid ${B.violet}`:"3px solid transparent",
            }}>
              {t.charAt(0).toUpperCase()+t.slice(1)}
            </button>
          ))}
        </div>

        {/* Vibes tab */}
        {activeTab==="vibes"&&(
          vibes.length>0 ? vibes.map((vibe,i)=>{
            const cc=catColor[vibe.category]||B.violet;
            return (
              <div key={vibe._id||i} style={{ display:"flex",gap:12,padding:"14px 16px",borderBottom:`1px solid ${B.border2}`,cursor:"pointer" }}
                onMouseEnter={e=>e.currentTarget.style.background="rgba(124,58,237,0.04)"}
                onMouseLeave={e=>e.currentTarget.style.background="none"}>
                <Avatar user={displayProfile} size={40} />
                <div style={{ flex:1 }}>
                  {vibe.category&&vibe.category!=="General"&&<div style={{ marginBottom:6 }}><VybePill label={vibe.category} color={cc} /></div>}
                  <div style={{ display:"flex",gap:6,alignItems:"center",marginBottom:4 }}>
                    <span style={{ fontWeight:700,fontSize:14,color:B.text }}>{displayProfile?.displayName}</span>
                    {displayProfile?.verified&&<span style={{ color:B.violet,fontSize:12 }}>✓</span>}
                    <span style={{ color:B.text3 }}>·</span>
                    <span style={{ fontSize:13,color:B.text2 }}>{timeAgo(vibe.createdAt)}</span>
                  </div>
                  <div style={{ fontSize:15,lineHeight:1.65,color:B.text }}>
                    {vibe.content?.split(/(\s+)/).map((w,j)=>
                      w.startsWith("#")?<span key={j} style={{color:B.violetLt}}>{w}</span>
                      :w.startsWith("@")?<span key={j} style={{color:B.vibeGreen}}>{w}</span>:w
                    )}
                  </div>
                  <div style={{ display:"flex",gap:6,marginTop:8 }}>
                    {[
                      {icon:ICONS.heart,count:vibe.likes?.length||0,color:B.coral},
                      {icon:ICONS.repeat,count:vibe.reposts?.length||0,color:B.vibeGreen},
                      {icon:ICONS.comment,count:vibe.replies?.length||0,color:B.sky},
                    ].map((a,j)=>(
                      <span key={j} style={{ display:"flex",alignItems:"center",gap:4,color:B.text2,fontSize:13 }}>
                        <Ic d={a.icon} s={14} c={B.text2} /> {numFmt(a.count)}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            );
          }) : (
            <div style={{ padding:48,textAlign:"center" }}>
              <div style={{ fontSize:40,marginBottom:12 }}>⚡</div>
              <div style={{ fontWeight:700,fontSize:18,color:B.text,marginBottom:6 }}>No vibes yet</div>
              <div style={{ color:B.text2 }}>
                {isMe ? "Post your first vibe to get started!" : `${displayProfile?.displayName} hasn't vibed yet`}
              </div>
            </div>
          )
        )}
        {activeTab!=="vibes"&&(
          <div style={{ padding:48,textAlign:"center" }}>
            <div style={{ fontSize:40,marginBottom:12 }}>✨</div>
            <div style={{ fontWeight:700,fontSize:18,color:B.text,marginBottom:6 }}>Coming soon</div>
            <div style={{ color:B.text2 }}>This tab is under construction</div>
          </div>
        )}
      </div>
    </div>
  );
}
